import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { FiLogIn, FiKey, FiX, FiEye, FiEyeOff, FiUser, FiLock, FiCheck, FiBarChart2, FiTarget, FiUsers, FiActivity, FiTrendingUp, FiCheckCircle } from "react-icons/fi";
import { useRole } from "../../context/RoleContext";
import "./PmsLogin.css";

// ===== OTP DISABLED (temporary) - START =====
// import { loginUser, verifyLoginOtp, resendLoginOtp, changePassword, getPublicLoginOptions } from "../../services/authApi";
// ===== OTP DISABLED (temporary) - END =====
import {
  loginUser,
  loginWithMicrosoft,
  changePassword,
  getPublicLoginOptions,
  passwordPolicy,
  PASSWORD_MIN_LENGTH,
  PASSWORD_MAX_LENGTH,
} from "../../services/authApi";
import { ENTRA, isEntraConfigured } from "../../services/entraAuth"; // [NEW] PM-12

// Match the backend .env: LOGIN_IP_WINDOW_SECONDS=300, LOGIN_MAX_FAILURES=5, LOGIN_LOCK_MINUTES=15
const LOGIN_WINDOW_MINUTES = 5;
const LOGIN_MAX_FAILURES = 5;
const LOGIN_LOCK_MINUTES = 15;

function getSsoStartUrl() {
  return "#";
}

async function exchangeSsoCode() {
  throw new Error("Single sign-on is not available. Sign in with your username and password.");
}

function getDefaultRoute() {
  return "/dashboard";
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84c-.21 1.13-.84 2.09-1.8 2.73v2.27h2.91c1.7-1.57 2.69-3.88 2.69-6.64z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.17l-2.91-2.27c-.81.54-1.84.86-3.05.86-2.35 0-4.34-1.58-5.05-3.71H.96v2.33C2.44 15.98 5.48 18 9 18z" />
      <path fill="#FBBC05" d="M3.95 10.71A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.27-1.71V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.04l2.99-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0 5.48 0 2.44 2.02.96 4.96l2.99 2.33C4.66 5.16 6.65 3.58 9 3.58z" />
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

function KeycloakIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="1" y="1" width="22" height="22" rx="5" fill="#E8590C" />
      <path
        d="M12 5.5 6.5 8.7v6.6L12 18.5l5.5-3.2V8.7L12 5.5Zm0 2.3 3.4 2v4l-3.4 2-3.4-2v-4l3.4-2Z"
        fill="#FFFFFF"
      />
    </svg>
  );
}

function getProviderIcon(providerType) {
  switch ((providerType || "").toLowerCase()) {
    case "google":
      return <GoogleIcon />;
    case "microsoft":
      return <MicrosoftIcon />;
    case "oidc":
      return <KeycloakIcon />;
    default:
      return <FiLogIn size={16} />;
  }
}

function BrandMark() {
  return (
    <svg width="38" height="42" viewBox="0 0 38 42" aria-hidden="true" className="pmsx-logo-svg">
      <polygon points="19,1 36,10.5 36,31.5 19,41 2,31.5 2,10.5" fill="#ffffff" fillOpacity="0.14" stroke="#ffffff" strokeWidth="1.8" />
      <rect x="10" y="22" width="4" height="9" rx="1" fill="#ffffff" />
      <rect x="17" y="16" width="4" height="15" rx="1" fill="#ffffff" />
      <rect x="24" y="10" width="4" height="21" rx="1" fill="#4cc3ff" />
    </svg>
  );
}

function Rule({ ok, children }) {
  return (
    <li className={ok ? "is-ok" : ""}>
      {ok ? <FiCheck size={12} aria-hidden="true" /> : <FiX size={12} aria-hidden="true" />}
      <span>{children}</span>
      <span className="pmsx-sr-only">{ok ? " (done)" : " (required)"}</span>
    </li>
  );
}

const FIXED_SSO_PROVIDER_TYPES = ["google", "microsoft", "oidc"];
const FIXED_SSO_PROVIDER_LABELS = {
  google: "Sign in with Google",
  microsoft: "Sign in with Microsoft",
  oidc: "Sign in with Keycloak",
};

const SSO_ENABLED = false;
// [UPDATED] PM-12: "Sign in with Microsoft" (Microsoft Entra ID through MSAL, backend POST /auth/entra/exchange).
// Shown when REACT_APP_ENTRA_ENABLED=true and the client id, tenant id and API scope are set in .env.
const MICROSOFT_SIGN_IN = isEntraConfigured();

const CAPTCHA_CHARACTERS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
const DEFAULT_ADMIN_USER_IDS_BY_GROUP_STORAGE_KEY = "settings_default_admin_user_ids_by_group";

const DEFAULT_LOGIN_SLUG = process.env.REACT_APP_PMS_LOGIN_SLUG || process.env.REACT_APP_LOGIN_SLUG || "";

function createCaptcha() {
  let value = "";
  for (let index = 0; index < 5; index += 1) {
    value += CAPTCHA_CHARACTERS.charAt(Math.floor(Math.random() * CAPTCHA_CHARACTERS.length));
  }
  return value;
}

function getLoginErrorMessage(error) {
  const status = error?.response?.status;
  const code = error?.response?.data?.error?.code;
  const backendMessage = error?.response?.data?.error?.message || error?.response?.data?.detail;
  if (status === 422) return "Enter a valid username (letters, digits, . _ @ -) and password.";
  if (status === 429) {
    const secs = Number(error?.response?.headers?.["retry-after"]);
    if (secs > 0) {
      const mins = Math.ceil(secs / 60);
      return `Too many sign-in attempts. Please try again in ${mins} minute${mins > 1 ? "s" : ""}.`;
    }
    return `Too many sign-in attempts. Please try again in ${LOGIN_WINDOW_MINUTES} minutes.`;
  }
  if (code === "INVALID_CREDENTIALS") {
    return `Invalid username or password. After ${LOGIN_MAX_FAILURES} failed attempts the account is locked for ${LOGIN_LOCK_MINUTES} minutes.`;
  }
  if (backendMessage) return backendMessage;
  if (error?.request) {
    return "Unable to connect to the authentication service. Please try again shortly.";
  }
  if (error instanceof Error && error.message) return error.message;
  return "Login failed. Please try again.";
}

function getSsoErrorMessage(ssoError) {
  switch (ssoError) {
    case "cancelled":
      return "Sign-in was cancelled.";
    case "provider_not_available":
      return "That sign-in method is no longer available for this group.";
    case "access_denied":
      return "Your account isn't authorized to sign in to this group.";
    default:
      return "Single sign-on failed. Please try again.";
  }
}

function registerDefaultAdminFromLogin(user) {
  if (user?.id == null) return;

  const eligibleRoles = (user.roles || []).filter(
    (role) => role && typeof role === "object" && role.is_default_admin === true
  );
  if (eligibleRoles.length === 0) return;

  const fallbackGroupIds = (user.groups || [])
    .map((group) => group.id ?? group.group_id)
    .filter((id) => id != null)
    .map(String);
  const groupIds = new Set(
    eligibleRoles.flatMap((role) => {
      const groupId = role.group_id ?? role.group?.id;
      return groupId != null ? [String(groupId)] : fallbackGroupIds;
    })
  );
  if (groupIds.size === 0) return;

  try {
    const raw = localStorage.getItem(DEFAULT_ADMIN_USER_IDS_BY_GROUP_STORAGE_KEY);
    const stored = raw ? JSON.parse(raw) : {};
    const assignments = stored && typeof stored === "object" && !Array.isArray(stored) ? { ...stored } : {};
    let changed = false;
    groupIds.forEach((groupId) => {
      if (assignments[groupId] == null) {
        assignments[groupId] = String(user.id);
        changed = true;
      }
    });
    if (changed) {
      localStorage.setItem(DEFAULT_ADMIN_USER_IDS_BY_GROUP_STORAGE_KEY, JSON.stringify(assignments));
    }
  } catch {
  }
}

export default function PMSLoginNew() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [captcha, setCaptcha] = useState("");
  const [captchaValue, setCaptchaValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // ===== OTP DISABLED (temporary) - START =====
  // const [otpChallenge, setOtpChallenge] = useState(null);
  // const [otp, setOtp] = useState("");
  // const [otpBusy, setOtpBusy] = useState(false);
  // const [otpSecondsLeft, setOtpSecondsLeft] = useState(0);
  // ===== OTP DISABLED (temporary) - END =====
  const emailInputRef = useRef(null);

  const { loginSlug: routeLoginSlug } = useParams();
  const loginSlug = routeLoginSlug || DEFAULT_LOGIN_SLUG;
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { setSession } = useRole();

  const hasPendingSsoCallback = (() => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search);
    return params.has("sso_code") || params.has("sso_error");
  })();

  const [optionsLoading, setOptionsLoading] = useState(Boolean(loginSlug) && !hasPendingSsoCallback);
  const [ssoProcessing, setSsoProcessing] = useState(hasPendingSsoCallback);
  const [loginGroup, setLoginGroup] = useState(null);
  const [passwordLoginEnabled, setPasswordLoginEnabled] = useState(true);
  const [ssoProviders, setSsoProviders] = useState([]);
  const ssoExchangeStartedRef = useRef(false);

  const showSsoOptions = SSO_ENABLED && searchParams.get("view") === "signin-options";

  const openSsoOptions = () => {
    const next = new URLSearchParams(searchParams);
    next.set("view", "signin-options");
    setSearchParams(next);
  };

  const closeSsoOptions = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("view");
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (!showSsoOptions) return;
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeSsoOptions();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSsoOptions]);

  const generateCaptcha = () => {
    setCaptchaLoading(true);
    setCaptchaValue(createCaptcha());
    setCaptcha("");
    setTimeout(() => setCaptchaLoading(false), 800);
  };

  const clearForm = () => {
    setEmail("");
    setPassword("");
    setCaptcha("");
    generateCaptcha();
  };

  useEffect(() => {
    generateCaptcha();
    const notice = sessionStorage.getItem("pms_login_notice");
    if (notice) {
      sessionStorage.removeItem("pms_login_notice");
      toast.info(notice);
    }
  }, []);

  const [passwordChange, setPasswordChange] = useState(null);

  // ===== OTP DISABLED (temporary) - START =====
  // useEffect(() => {
  //   if (!otpChallenge?.expiresAt) return undefined;
  //   const update = () => setOtpSecondsLeft(Math.max(0, Math.ceil((otpChallenge.expiresAt - Date.now()) / 1000)));
  //   update();
  //   const timer = window.setInterval(update, 1000);
  //   return () => window.clearInterval(timer);
  // }, [otpChallenge]);
  //
  // const beginOtp = (data) => {
  //   setOtp("");
  //   setOtpChallenge({
  //     token: data.challenge_token,
  //     destination: data.destination || "your registered email address",
  //     expiresAt: Date.now() + Number(data.expires_in || 600) * 1000,
  //   });
  //   toast.info(data.message || "A verification code was sent to your registered email address.");
  // };
  // ===== OTP DISABLED (temporary) - END =====

  const completeLogin = (data) => {
    if (!data?.access_token || !data?.user) {
      throw new Error("Authentication service returned an incomplete login response.");
    }
    const { user } = data;
    localStorage.setItem("access_token", data.access_token);
    localStorage.setItem("user", JSON.stringify(user));
    localStorage.setItem("roles", JSON.stringify(user.roleKeys || []));

    if (user.mustChangePassword) {
      setPasswordChange({ username: user.username, current: password, next: "", confirm: "", busy: false });
      return;
    }

    setPassword("");

    registerDefaultAdminFromLogin(user);
    localStorage.setItem("pms_role", user.roleKey);
    localStorage.setItem("pms_username", user.username);
    setSession(user.username, user.roleKey);

    const defaultRoute = getDefaultRoute();
    localStorage.setItem("lastRoute", defaultRoute);
    toast.success(`Welcome, ${user.name || user.username}!`);
    setTimeout(() => {
      navigate(location.state?.from?.pathname || defaultRoute, { replace: true });
    }, 300);
  };

  const handleLoginResult = (data) => {
    // ===== OTP DISABLED (temporary) - START =====
    // if (data?.mfa_required) {
    //   beginOtp(data);
    //   return;
    // }
    // ===== OTP DISABLED (temporary) - END =====
    completeLogin(data);
  };

  // ===== OTP DISABLED (temporary) - START =====
  // const submitOtp = async () => {
  //   if (!/^[0-9]{6}$/.test(otp)) {
  //     toast.error("Enter the six-digit verification code from your email.");
  //     return;
  //   }
  //   if (otpSecondsLeft <= 0) {
  //     toast.error("This verification code has expired. Sign in again to request a new one.");
  //     return;
  //   }
  //   setOtpBusy(true);
  //   try {
  //     const { data } = await verifyLoginOtp({ challengeToken: otpChallenge.token, otp });
  //     setOtpChallenge(null);
  //     setOtp("");
  //     completeLogin(data);
  //   } catch (error) {
  //     toast.error(getLoginErrorMessage(error));
  //   } finally {
  //     setOtpBusy(false);
  //   }
  // };
  //
  // const resendOtp = async () => {
  //   setOtpBusy(true);
  //   try {
  //     const { data } = await resendLoginOtp({ challengeToken: otpChallenge.token });
  //     setOtp("");
  //     setOtpChallenge((current) => ({
  //       ...current,
  //       destination: data.destination || current.destination,
  //       expiresAt: Date.now() + Number(data.expires_in || 600) * 1000,
  //     }));
  //     toast.success(data.message || "A new verification code has been queued.");
  //   } catch (error) {
  //     toast.error(getLoginErrorMessage(error));
  //   } finally {
  //     setOtpBusy(false);
  //   }
  // };
  //
  // const cancelOtp = () => {
  //   setOtpChallenge(null);
  //   setOtp("");
  //   setPassword("");
  //   generateCaptcha();
  // };
  // ===== OTP DISABLED (temporary) - END =====

  const submitPasswordChange = async () => {
    const form = passwordChange;
    const rules = passwordPolicy(form.next);
    if (!rules.length || !rules.lowercase || !rules.uppercase || !rules.digit || !rules.special || !rules.noSpaces) { toast.error("Use 8 to 128 characters with uppercase, lowercase, a digit, and a special character; spaces are not allowed."); return; }
    if (form.next !== form.confirm) { toast.error("The two new passwords don't match."); return; }
    setPasswordChange({ ...form, busy: true });
    try {
      await changePassword({ currentPassword: form.current, newPassword: form.next });
      const { data } = await loginUser({ username: form.username, password: form.next });
      setPasswordChange(null);
      setPassword(form.next);
      handleLoginResult(data);
    } catch (error) {
      toast.error(getLoginErrorMessage(error));
      setPasswordChange({ ...form, busy: false });
    }
  };

  useEffect(() => {
    const ssoCode = searchParams.get("sso_code");
    const ssoError = searchParams.get("sso_error");

    if (ssoCode || ssoError) {
      navigate(location.pathname, { replace: true });
    }

    if (ssoError) {
      toast.error(getSsoErrorMessage(ssoError));
    }

    if (ssoCode) {
      if (ssoExchangeStartedRef.current) return;
      ssoExchangeStartedRef.current = true;
      setSsoProcessing(true);
      exchangeSsoCode({ code: ssoCode })
        .then(({ data }) => completeLogin(data))
        .catch((error) => {
          console.error("SSO exchange failed:", error?.message || error);
          toast.error(getLoginErrorMessage(error));
          setSsoProcessing(false);
        });
      return;
    }

    if (!loginSlug) {
      setOptionsLoading(false);
      return;
    }

    let cancelled = false;
    setOptionsLoading(true);
    getPublicLoginOptions(loginSlug)
      .then(({ data }) => {
        if (cancelled) return;
        setLoginGroup(data?.group ?? null);
        setPasswordLoginEnabled(data?.password_login_enabled !== false);
        setSsoProviders(Array.isArray(data?.providers) ? data.providers : []);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Failed to load login options:", error?.message || error);
        if (routeLoginSlug) {
          toast.error("This sign-in page could not be found. Falling back to standard login.");
        }
        setPasswordLoginEnabled(true);
        setSsoProviders([]);
      })
      .finally(() => {
        if (!cancelled) setOptionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginSlug]);

  const handleProviderClick = (provider) => {
    const returnTo = `${window.location.origin}${location.pathname}`;
    window.location.href = getSsoStartUrl(loginSlug, provider.id, returnTo);
  };

  const displayProviders = FIXED_SSO_PROVIDER_TYPES.map((type) => {
    const configured = ssoProviders.find((provider) => (provider.provider_type || "").toLowerCase() === type);
    return {
      type,
      configured,
      label: configured?.display_name || FIXED_SSO_PROVIDER_LABELS[type],
    };
  });

  const handleFixedProviderClick = (entry) => {
    if (!entry.configured) {
      toast.error(`${entry.label} isn't set up for this account yet. Contact your administrator.`);
      return;
    }
    handleProviderClick(entry.configured);
  };

  // [NEW] PM-12: Microsoft sign-in. The backend links the Microsoft account to the PMS employee by work e-mail.
  const microsoftLogin = async () => {
    setIsSubmitting(true);
    try {
      const { data } = await loginWithMicrosoft();
      handleLoginResult(data);
    } catch (error) {
      const code = error?.response?.data?.error?.code;
      const text = {
        SSO_IDENTITY_NOT_LINKED: "Your Microsoft account is not linked to an active PMS employee. Ask HR to check that your work e-mail in PMS matches your Microsoft account.",
        SSO_UNAVAILABLE: "Microsoft sign-in is not switched on in the PMS server yet. Use your e-mail and password.",
        SSO_PROVIDER_UNAVAILABLE: "Microsoft sign-in is not reachable right now. Try again in a minute or use your e-mail and password.",
        SSO_SCOPE_REQUIRED: "The Microsoft sign-in is set up without the PMS API permission. Contact IT.",
        SSO_TOKEN_INVALID: "Microsoft sign-in could not be verified. Contact IT if this keeps happening.",
        SSO_TOKEN_EXPIRED: "Your Microsoft sign-in expired. Please try again.",
      }[code];
      toast.error(text || getLoginErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const login = async () => {
    const normalizedEmail = email.trim();
    const normalizedPassword = password;
    const normalizedCaptcha = captcha.trim();

    if (!normalizedEmail) {
      toast.error("Username is required.");
      clearForm();
      return;
    }

    if (!normalizedPassword) {
      toast.error("Password is required.");
      clearForm();
      return;
    }

    if (!normalizedCaptcha) {
      toast.error("Captcha is required.");
      clearForm();
      return;
    }

    if (normalizedCaptcha.toUpperCase() !== captchaValue) {
      toast.error("Invalid captcha. Please try again.");
      clearForm();
      return;
    }

    setIsSubmitting(true);

    try {
      const { data } = await loginUser({ username: normalizedEmail, password });
      handleLoginResult(data);
    } catch (error) {
      console.error("Login request failed:", error?.message || error);
      toast.error(getLoginErrorMessage(error));
      clearForm();
    } finally {
      setIsSubmitting(false);
    }
  };

  // Live password rules for the forced "set a new password" screen.
  const changeRules = passwordChange
    ? {
        ...passwordPolicy(passwordChange.next),
        match: passwordChange.next.length > 0 && passwordChange.next === passwordChange.confirm,
      }
    : null;

  const canChangePassword = Boolean(
    changeRules &&
    changeRules.length &&
    changeRules.lowercase &&
    changeRules.uppercase &&
    changeRules.digit &&
    changeRules.special &&
    changeRules.noSpaces &&
    changeRules.match &&
    !passwordChange.busy
  );

  const showLoading = ssoProcessing;
  const showNoLoginMethod = !showLoading && !optionsLoading && ssoProviders.length === 0 && !passwordLoginEnabled;

  return (
    <main className="pmsx-root" aria-label="Teliolabs PMS sign in">
      <div className="pmsx-decor" aria-hidden="true">
        <span className="pmsx-hex pmsx-hex-1"><FiBarChart2 size={22} /></span>
        <span className="pmsx-hex pmsx-hex-2"><FiTarget size={20} /></span>
        <span className="pmsx-hex pmsx-hex-3"><FiUsers size={20} /></span>
        <span className="pmsx-hex pmsx-hex-4"><FiActivity size={22} /></span>
        <span className="pmsx-hex pmsx-hex-5"><FiTrendingUp size={20} /></span>
        <span className="pmsx-hex pmsx-hex-6"><FiCheckCircle size={18} /></span>
      </div>
      <section className="pmsx-panel">
        <div className="pmsx-brand">
          <BrandMark />
          <span className="pmsx-brand-name">TELIOLABS</span>
        </div>

        <div className="pmsx-heading">
          <h1 className="pmsx-title">Welcome to PMS</h1>
          <p className="pmsx-subtitle">Sign in to Performance Management System</p>
          {loginGroup?.name ? <p className="pmsx-group-name">{loginGroup.name}</p> : null}
        </div>

        {passwordChange ? (
          <div className="pmsx-form">
            <p className="pmsx-note">Set a new password to continue.</p>
            <div className="pmsx-field">
              <label className="pmsx-sr-only" htmlFor="pmsx-new-password">New password</label>
              <FiLock className="pmsx-field-icon" size={17} aria-hidden="true" />
              <input
                id="pmsx-new-password"
                type={showNewPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder="New password"
                value={passwordChange.next}
                maxLength={PASSWORD_MAX_LENGTH}
                onChange={(event) => setPasswordChange({ ...passwordChange, next: event.target.value })}
                disabled={passwordChange.busy}
                autoFocus
                className="pmsx-has-toggle"
              />
              <button
                type="button"
                className="pmsx-password-toggle"
                onClick={() => setShowNewPassword((prev) => !prev)}
                disabled={passwordChange.busy}
                aria-label={showNewPassword ? "Hide password" : "Show password"}
                aria-pressed={showNewPassword}
                tabIndex={-1}
              >
                {showNewPassword ? <FiEye size={17} /> : <FiEyeOff size={17} />}
              </button>
            </div>
            <div className="pmsx-field">
              <label className="pmsx-sr-only" htmlFor="pmsx-confirm-password">Confirm new password</label>
              <FiLock className="pmsx-field-icon" size={17} aria-hidden="true" />
              <input
                id="pmsx-confirm-password"
                type={showConfirmPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder="Confirm new password"
                value={passwordChange.confirm}
                maxLength={PASSWORD_MAX_LENGTH}
                onChange={(event) => setPasswordChange({ ...passwordChange, confirm: event.target.value })}
                onKeyDown={(event) => { if (event.key === "Enter" && canChangePassword) submitPasswordChange(); }}
                disabled={passwordChange.busy}
                className="pmsx-has-toggle"
              />
              <button
                type="button"
                className="pmsx-password-toggle"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                disabled={passwordChange.busy}
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                aria-pressed={showConfirmPassword}
                tabIndex={-1}
              >
                {showConfirmPassword ? <FiEye size={17} /> : <FiEyeOff size={17} />}
              </button>
            </div>

            <ul className="pmsx-rules" aria-label="Password rules">
              <Rule ok={changeRules.length}>{PASSWORD_MIN_LENGTH}-{PASSWORD_MAX_LENGTH} characters</Rule>
              <Rule ok={changeRules.lowercase}>Lowercase</Rule>
              <Rule ok={changeRules.uppercase}>Uppercase</Rule>
              <Rule ok={changeRules.digit}>Digit</Rule>
              <Rule ok={changeRules.special}>Special character</Rule>
              <Rule ok={changeRules.noSpaces}>No spaces</Rule>
              <Rule ok={changeRules.match}>Passwords match</Rule>
            </ul>

            <button type="button" className="pmsx-login-btn" onClick={submitPasswordChange} disabled={!canChangePassword}>
              {passwordChange.busy ? "Saving..." : "Change password and sign in"}
            </button>
            <button
              type="button"
              className="pmsx-secondary-btn"
              onClick={() => { localStorage.removeItem("access_token"); setPasswordChange(null); clearForm(); }}
              disabled={passwordChange.busy}
            >
              <span>Cancel</span>
            </button>
          </div>
        ) :
        // ===== OTP DISABLED (temporary) - START =====
        // otpChallenge ? (
        //   <div className="pmsx-form">
        //     <p className="pmsx-note">
        //       Enter the six-digit code sent to <strong>{otpChallenge.destination}</strong>.
        //     </p>
        //     <div className="pmsx-field">
        //       <label className="pmsx-sr-only" htmlFor="pmsx-login-otp">Verification code</label>
        //       <FiKey className="pmsx-field-icon" size={17} aria-hidden="true" />
        //       <input
        //         id="pmsx-login-otp"
        //         type="text"
        //         inputMode="numeric"
        //         autoComplete="one-time-code"
        //         placeholder="6-digit verification code"
        //         value={otp}
        //         maxLength={6}
        //         onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))}
        //         onKeyDown={(event) => { if (event.key === "Enter") submitOtp(); }}
        //         disabled={otpBusy}
        //         autoFocus
        //       />
        //     </div>
        //     <p className="pmsx-note">
        //       {otpSecondsLeft > 0
        //         ? `Code expires in ${Math.floor(otpSecondsLeft / 60)}:${String(otpSecondsLeft % 60).padStart(2, "0")}.`
        //         : "This code has expired. Sign in again to request a new one."}
        //     </p>
        //     <button type="button" className="pmsx-login-btn" onClick={submitOtp}
        //       disabled={otpBusy || otpSecondsLeft <= 0}>
        //       {otpBusy ? "Verifying..." : "Verify and sign in"}
        //     </button>
        //     <button type="button" className="pmsx-secondary-btn" onClick={resendOtp}
        //       disabled={otpBusy || otpSecondsLeft <= 0}>
        //       <span>Resend code</span>
        //     </button>
        //     <button type="button" className="pmsx-secondary-btn" onClick={cancelOtp} disabled={otpBusy}>
        //       <span>Back to sign in</span>
        //     </button>
        //   </div>
        // ) :
        // ===== OTP DISABLED (temporary) - END =====
        showLoading ? (
          <div className="pmsx-loading">
            <span className="pmsx-spinner" />
            <span>Completing sign-in...</span>
          </div>
        ) : showNoLoginMethod ? (
          <div className="pmsx-error-box">
            <span className="pmsx-error-icon">!</span>
            <span>No sign-in method is currently available for this group. Please contact your administrator.</span>
          </div>
        ) : (
          <div className="pmsx-form">
            {showSsoOptions ? (
              <div className="pmsx-sso">
                <button
                  type="button"
                  className="pmsx-sso-close"
                  onClick={closeSsoOptions}
                  disabled={isSubmitting}
                  aria-label="Close sign-in options"
                >
                  <FiX size={16} />
                </button>

                {displayProviders.map((entry) => (
                  <button
                    key={entry.type}
                    type="button"
                    className="pmsx-sso-btn"
                    onClick={() => handleFixedProviderClick(entry)}
                    disabled={isSubmitting}
                  >
                    {getProviderIcon(entry.type)}
                    <span>{entry.label}</span>
                  </button>
                ))}
              </div>
            ) : (
              <>
                {MICROSOFT_SIGN_IN && (
                  <>
                    <button
                      type="button"
                      className="pmsx-sso-btn pmsx-sso-primary"
                      onClick={microsoftLogin}
                      disabled={isSubmitting}
                    >
                      {getProviderIcon("microsoft")}
                      <span>{isSubmitting ? "Signing in…" : ENTRA.label}</span>
                    </button>
                    {passwordLoginEnabled && <div className="pmsx-or"><span>or use e-mail and password</span></div>}
                  </>
                )}
                {passwordLoginEnabled && (
                  <>
                    <div className="pmsx-field">
                      <label className="pmsx-sr-only" htmlFor="pmsx-username">Email</label>
                      <FiUser className="pmsx-field-icon" size={17} aria-hidden="true" />
                      <input
                        id="pmsx-username"
                        ref={emailInputRef}
                        type="text"
                        autoComplete="username"
                        placeholder="Email"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        disabled={isSubmitting}
                      />
                    </div>

                    <div className="pmsx-field">
                      <label className="pmsx-sr-only" htmlFor="pmsx-password">Password</label>
                      <FiLock className="pmsx-field-icon" size={17} aria-hidden="true" />
                      <input
                        id="pmsx-password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        placeholder="Password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") login();
                        }}
                        disabled={isSubmitting}
                        className="pmsx-has-toggle"
                      />
                      <button
                        type="button"
                        className="pmsx-password-toggle"
                        onClick={() => setShowPassword((prev) => !prev)}
                        disabled={isSubmitting}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        aria-pressed={showPassword}
                        tabIndex={-1}
                      >
                        {showPassword ? <FiEye size={17} /> : <FiEyeOff size={17} />}
                      </button>
                    </div>

                    <div className="pmsx-forgot">
                      <Link to="/forgot-password" className="pmsx-forgot-link">Forgot Password?</Link>
                    </div>

                    <div className="pmsx-divider" />

                    <div className="pmsx-captcha-row">
                      <div className="pmsx-captcha-box" aria-label="Captcha">
                        {captchaValue.split("").map((char, i) => (
                          <span
                            key={i}
                            className="pmsx-captcha-char"
                            style={{ transform: `rotate(${(i - 2) * 6}deg)` }}
                          >
                            {char}
                          </span>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="pmsx-refresh"
                        onClick={generateCaptcha}
                        disabled={isSubmitting}
                        aria-label="Generate a new captcha"
                      >
                        <span className={`pmsx-refresh-icon${captchaLoading ? " spin" : ""}`}>↻</span>
                      </button>
                      <input
                        placeholder="Enter captcha"
                        aria-label="Enter captcha"
                        value={captcha}
                        onChange={(event) => setCaptcha(event.target.value.toUpperCase())}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") login();
                        }}
                        disabled={isSubmitting}
                        className="pmsx-captcha-input"
                      />
                    </div>

                    <button type="button" className="pmsx-login-btn" onClick={login} disabled={isSubmitting}>
                      {isSubmitting ? (
                        <span className="pmsx-btn-loading">
                          <span className="pmsx-spinner" />
                          <span>Signing in...</span>
                        </span>
                      ) : (
                        "Login"
                      )}
                    </button>
                  </>
                )}

                {SSO_ENABLED && (optionsLoading ? (
                  <div className="pmsx-loading">
                    <span className="pmsx-spinner" />
                    <span>Loading sign-in options...</span>
                  </div>
                ) : (
                  <>
                    <div className="pmsx-or"><span>OR</span></div>
                    <button
                      type="button"
                      className="pmsx-secondary-btn"
                      onClick={openSsoOptions}
                      disabled={isSubmitting}
                      aria-expanded={showSsoOptions}
                    >
                      <FiKey size={16} />
                      <span>Sign-in options</span>
                    </button>
                  </>
                ))}
              </>
            )}
          </div>
        )}
      </section>

      {!showSsoOptions && (
        <footer className="pmsx-footer">
          <p>Not able to login? Contact HR Team</p>
          <a href="mailto:HR@teliolabs.com">HR@teliolabs.com</a>
          <p className="pmsx-copyright">&copy; 2026 Teliolabs. All rights reserved.</p>
        </footer>
      )}
    </main>
  );
}